import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp, MaskLine } from "@/components/motion";
import { LOCATIONS, OTHER_AREAS, IMAGES } from "@/data/content";

export default function Areas() {
  const capitals = LOCATIONS.filter((l) => l.region === "Capital City");
  const regional = LOCATIONS.filter((l) => l.region !== "Capital City");

  return (
    <main className="pt-20">
      <SEO
        title="Areas We Service"
        description="NextMove Removalists services every Australian capital and major regional hub: Sydney, Melbourne, Brisbane, Perth, Adelaide, Canberra, Hobart, Darwin and beyond."
        path="/areas"
      />
      <section className="relative overflow-hidden bg-navy grain py-16 sm:py-24">
        <img src={IMAGES.city} alt="Australian city skyline connected by transit network"
          className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">Coverage</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white">
            <MaskLine delay={0.1}>Wherever you're headed,</MaskLine>
            <MaskLine delay={0.22}><span className="text-amber-400">we already drive there.</span></MaskLine>
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300">
              Eight capitals, seven major regional hubs, and daily linehaul between them.
              Pick your city for local routes, access know-how and corridor schedules.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 sm:py-24">
        <FadeUp>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">Capital cities</h2>
        </FadeUp>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {capitals.map((l, i) => (
            <FadeUp key={l.slug} delay={i * 0.05}>
              <Link to={`/areas/${l.slug}`} data-testid={`areas-card-${l.slug}`}
                className="card-lift group block h-full rounded-2xl border border-slate-200 bg-white p-7">
                <MapPin className="h-5 w-5 text-amber-500" />
                <h3 className="mt-4 font-display text-xl font-bold text-navy">{l.city}</h3>
                <p className="text-xs font-mono uppercase tracking-wider text-slate-400">{l.state}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 line-clamp-3">{l.intro}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-amber-600">
                  View area <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-16">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">Regional hubs</h2>
        </FadeUp>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {regional.map((l, i) => (
            <FadeUp key={l.slug} delay={i * 0.05}>
              <Link to={`/areas/${l.slug}`} data-testid={`areas-card-${l.slug}`}
                className="card-lift group block h-full rounded-2xl border border-slate-200 bg-white p-7">
                <MapPin className="h-5 w-5 text-amber-500" />
                <h3 className="mt-4 font-display text-xl font-bold text-navy">{l.city}</h3>
                <p className="text-xs font-mono uppercase tracking-wider text-slate-400">{l.state}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 line-clamp-3">{l.intro}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-amber-600">
                  View area <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-16 rounded-2xl bg-slate-100 p-8 sm:p-10">
          <h3 className="font-display text-lg font-bold text-navy">Also on our routes</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{OTHER_AREAS}</p>
        </FadeUp>
      </section>
    </main>
  );
}
